import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentEmms } from "../../redux/slices/currentEmmsSlice";
import { setEmmsHeaders } from "../../redux/slices/emmsAuthSlice";
import TitleWithDropdown from "../TitleWithDropdown";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Pagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import SearchIcon from "@mui/icons-material/Search";
import StayCurrentPortraitIcon from "@mui/icons-material/StayCurrentPortrait";
import StayCurrentLandscapeIcon from "@mui/icons-material/StayCurrentLandscape";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import image from "../../logos/kioskStudio.png";
import "../../components/CommonDashboard.css";
import { KioskMiniPreview } from "./KioskTemplatesDashboard";
import { toPng } from "html-to-image";

function renderThumbNode(node, scale) {
  const s = node.styles || {};
  const baseStyle = {
    position: "absolute",
    left: (s.x || 0) * scale,
    top: (s.y || 0) * scale,
    width: (s.width || 0) * scale,
    height: (s.height || 0) * scale,
    overflow: "hidden",
    borderRadius: s.borderRadius ? s.borderRadius * scale : undefined,
    opacity: s.opacity != null ? s.opacity : 1,
    ...(s.gradientEnabled && s.gradientFrom && s.gradientTo
      ? { background: `linear-gradient(${s.gradientAngle || 135}deg, ${s.gradientFrom}, ${s.gradientTo})` }
      : { backgroundColor: s.backgroundColor || "transparent" }),
    ...(s.backgroundImage
      ? { backgroundImage: `url(${s.backgroundImage})`, backgroundSize: "cover", backgroundPosition: "center" }
      : {}),
  };

  if (node.type === "group") {
    return (
      <div key={node.id} style={baseStyle}>
        {(node.children || []).map((c) => renderThumbNode(c, scale))}
      </div>
    );
  }
  if (node.type === "text" || node.type === "button") {
    return (
      <div key={node.id} style={{ ...baseStyle, color: s.color || "#fff", fontSize: Math.max((s.fontSize || 12) * scale, 4), fontWeight: s.fontWeight || "normal", textAlign: s.textAlign || "left", display: "flex", alignItems: "center", padding: `${1 * scale}px` }}>
        <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "100%" }}>
          {node.content || (node.type === "button" ? "Button" : "")}
        </span>
      </div>
    );
  }
  if (node.type === "image") {
    return (
      <div key={node.id} style={baseStyle}>
        {(s.src || s.url) && <img src={s.src || s.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} crossOrigin="anonymous" />}
      </div>
    );
  }
  return <div key={node.id} style={baseStyle} />;
}

function ScreenThumb({ screen, selected, onToggle, onRef }) {
  const THUMB_W = 110;
  const scale = THUMB_W / (screen.styles?.width || 390);
  const thumbH = (screen.styles?.height || 844) * scale;

  return (
    <Box onClick={onToggle} sx={{ cursor: "pointer", userSelect: "none", flexShrink: 0 }}>
      <Box sx={{ position: "relative" }}>
        <Box
          ref={(el) => onRef(screen.id, el)}
          sx={{
            width: THUMB_W,
            height: thumbH,
            position: "relative",
            overflow: "hidden",
            backgroundColor: screen.styles?.backgroundColor || "#1a2228",
            borderRadius: 1,
            outline: selected ? "2.5px solid" : "1.5px solid",
            outlineColor: selected ? "primary.main" : "grey.300",
          }}
        >
          {(screen.children || []).map((node) => renderThumbNode(node, scale))}
        </Box>
        {selected && (
          <CheckCircleIcon
            sx={{ position: "absolute", top: 4, right: 4, fontSize: 18, color: "primary.main", background: "#fff", borderRadius: "50%" }}
          />
        )}
      </Box>
      <Typography variant="caption" align="center" display="block" sx={{ mt: 0.5, fontSize: 10, color: "text.secondary" }}>
        {screen.name}
      </Typography>
    </Box>
  );
}

const RESOLUTIONS = [
  { value: "390x844", label: "390 × 844 — Portrait Mobile" },
  { value: "1080x1920", label: "1080 × 1920 — Portrait Full HD" },
  { value: "768x1024", label: "768 × 1024 — Portrait Tablet" },
  { value: "1920x1080", label: "1920 × 1080 — Landscape Full HD" },
  { value: "1024x768", label: "1024 × 768 — Landscape Tablet" },
];

// Deterministic hash so same template ID always gets same palette + layout
function _cdHash(str) {
  let h = 5381;
  for (let i = 0; i < (str || "").length; i++) h = Math.imul((h << 5) + h, 1) ^ str.charCodeAt(i);
  return Math.abs(h);
}
const CD_PALETTES = [
  { bg: "#1a1a2e", a: "#e94560", b: "#0f3460", t: "#ffffff18" },
  { bg: "#0d1117", a: "#58a6ff", b: "#1f6feb", t: "#ffffff15" },
  { bg: "#1e1b4b", a: "#818cf8", b: "#312e81", t: "#ffffff18" },
  { bg: "#0f172a", a: "#22c55e", b: "#15803d", t: "#ffffff15" },
  { bg: "#1c1917", a: "#fb923c", b: "#c2410c", t: "#ffffff18" },
  { bg: "#0c1a2e", a: "#06b6d4", b: "#0e7490", t: "#ffffff15" },
  { bg: "#1a0a2e", a: "#a855f7", b: "#7c3aed", t: "#ffffff18" },
  { bg: "#0f2217", a: "#34d399", b: "#059669", t: "#ffffff15" },
];
// Each layout is a function (p=palette, w, h) => JSX
const CD_LAYOUTS = [
  // 0 — header bar + content blocks + CTA
  (p, w, h) => (
    <div style={{ width: w, height: h, background: p.bg, borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>
      <div style={{ height: h * 0.13, background: `linear-gradient(90deg,${p.a},${p.b})` }} />
      <div style={{ padding: "7px 9px", display: "flex", flexDirection: "column", gap: 5 }}>
        <div style={{ height: 7, background: p.a, borderRadius: 2, width: "60%" }} />
        <div style={{ height: 5, background: p.t, borderRadius: 2, width: "80%" }} />
        <div style={{ height: h * 0.18, background: p.b + "99", borderRadius: 4, marginTop: 3 }} />
        <div style={{ display: "flex", gap: 4 }}>
          <div style={{ flex: 1, height: h * 0.1, background: p.t, borderRadius: 3 }} />
          <div style={{ flex: 1, height: h * 0.1, background: p.t, borderRadius: 3 }} />
        </div>
        <div style={{ height: h * 0.09, background: p.a + "cc", borderRadius: 4, marginTop: 3 }} />
      </div>
    </div>
  ),
  // 1 — big hero gradient + grid cards
  (p, w, h) => (
    <div style={{ width: w, height: h, background: p.bg, borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>
      <div style={{ height: h * 0.38, background: `linear-gradient(140deg,${p.b},${p.a})`, display: "flex", alignItems: "flex-end", padding: "0 9px 7px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <div style={{ height: 7, background: "#fff5", borderRadius: 2, width: 60 }} />
          <div style={{ height: 5, background: "#fff3", borderRadius: 2, width: 44 }} />
        </div>
      </div>
      <div style={{ padding: "7px 9px", display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
          {[0, 1, 2, 3].map(i => <div key={i} style={{ height: h * 0.1, background: i % 2 === 0 ? p.b + "88" : p.t, borderRadius: 4 }} />)}
        </div>
        <div style={{ height: h * 0.09, background: p.a + "bb", borderRadius: 4 }} />
      </div>
    </div>
  ),
  // 2 — sidebar nav + main area
  (p, w, h) => (
    <div style={{ width: w, height: h, background: p.bg, borderRadius: 6, overflow: "hidden", flexShrink: 0, display: "flex" }}>
      <div style={{ width: "27%", background: p.b + "cc", display: "flex", flexDirection: "column", gap: 5, padding: "10px 6px" }}>
        <div style={{ height: 18, background: p.a + "99", borderRadius: 3 }} />
        {[0, 1, 2, 3].map(i => <div key={i} style={{ height: 11, background: p.t, borderRadius: 2 }} />)}
      </div>
      <div style={{ flex: 1, padding: "8px 7px", display: "flex", flexDirection: "column", gap: 5 }}>
        <div style={{ height: 6, background: p.a, borderRadius: 2, width: "65%" }} />
        <div style={{ height: h * 0.2, background: p.b + "66", borderRadius: 4 }} />
        <div style={{ display: "flex", gap: 4 }}>
          <div style={{ flex: 1, height: h * 0.12, background: p.t, borderRadius: 3 }} />
          <div style={{ flex: 1, height: h * 0.12, background: p.t, borderRadius: 3 }} />
        </div>
        <div style={{ height: h * 0.09, background: p.a + "cc", borderRadius: 3, marginTop: "auto" }} />
      </div>
    </div>
  ),
  // 3 — centered card / kiosk splash
  (p, w, h) => (
    <div style={{ width: w, height: h, background: `radial-gradient(ellipse at 50% 30%, ${p.b}, ${p.bg})`, borderRadius: 6, overflow: "hidden", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: 10 }}>
      <div style={{ width: 38, height: 38, borderRadius: "50%", background: `linear-gradient(135deg,${p.a},${p.b})`, boxShadow: `0 0 12px ${p.a}88` }} />
      <div style={{ height: 7, background: p.a, borderRadius: 2, width: "65%" }} />
      <div style={{ height: 5, background: p.t, borderRadius: 2, width: "75%" }} />
      <div style={{ height: 5, background: p.t, borderRadius: 2, width: "52%" }} />
      <div style={{ height: h * 0.09, background: p.a + "cc", borderRadius: 4, width: "68%", marginTop: 4 }} />
      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
        {[0, 1, 2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: i === 1 ? p.a : p.t }} />)}
      </div>
    </div>
  ),
  // 4 — info kiosk with top banner + QR-like block
  (p, w, h) => (
    <div style={{ width: w, height: h, background: p.bg, borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>
      <div style={{ height: h * 0.15, background: `linear-gradient(90deg,${p.a},${p.b})`, display: "flex", alignItems: "center", padding: "0 9px", gap: 5 }}>
        <div style={{ width: 14, height: 14, background: "#fff4", borderRadius: 2 }} />
        <div style={{ height: 6, background: "#fff5", borderRadius: 2, width: "42%" }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "9px 11px", gap: 5 }}>
        <div style={{ width: 52, height: 52, background: p.b + "88", border: `2px solid ${p.a}55`, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 28, height: 28, background: `repeating-linear-gradient(45deg,${p.a}33 0,${p.a}33 3px,transparent 3px,transparent 6px)`, borderRadius: 2 }} />
        </div>
        <div style={{ height: 7, background: p.a, borderRadius: 2, width: "58%" }} />
        <div style={{ height: 5, background: p.t, borderRadius: 2, width: "78%" }} />
        <div style={{ height: h * 0.09, background: p.a + "cc", borderRadius: 4, width: "62%", marginTop: 3 }} />
      </div>
    </div>
  ),
];

function CustomDesignPlaceholder({ id, width, height }) {
  const h = _cdHash(id);
  const p = CD_PALETTES[h % CD_PALETTES.length];
  const Layout = CD_LAYOUTS[(h >> 4) % CD_LAYOUTS.length];
  return Layout(p, width, height);
}

function StudioCard({ item, onEdit, onDelete, onPreview, onPublish, orientation }) {
  const isLandscape = orientation === "landscape";
  const cardWidth = isLandscape ? 270 : 190;
  const placeholderW = isLandscape ? 230 : 130;
  const placeholderH = isLandscape ? 130 : 230;

  const dateVal = item.savedAt || item.createdAt || item.updatedAt;
  const dateStr = dateVal
    ? new Date(dateVal).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";

  return (
    <Card
      elevation={2}
      sx={{
        width: cardWidth,
        borderRadius: 2,
        transition: "box-shadow 0.2s, width 0.2s",
        "&:hover": { boxShadow: 6 },
      }}
    >
      <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", pb: 0, pt: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, pt: 1, pb: 1 }}>
          <Chip label={item.name} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 500 }} />
        </Box>
        {item.sourceTemplate ? (
          <KioskMiniPreview template={item.sourceTemplate} orientation={orientation} />
        ) : (
          <CustomDesignPlaceholder id={item.id} width={placeholderW} height={placeholderH} />
        )}

      </CardContent>

      <CardActions sx={{ justifyContent: "space-between", px: 1.5, pb: 1.5, pt: 1, flexWrap: "wrap", gap: 0.5 }}>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Preview">
            <IconButton size="small" onClick={() => onPreview(item)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDelete(item.id)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {/* {item.purchasedAt === null && (
            <Tooltip title="Publish to Store">
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<StorefrontIcon fontSize="small" />}
                onClick={() => onPublish(item)}
                sx={{ fontSize: 10, textTransform: "none" }}
              >
                Publish
              </Button>
            </Tooltip>
          )} */}
          <Button
            size="small"
            variant="contained"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => onEdit(item)}
            sx={{ fontSize: 11, textTransform: "none" }}
          >
            Edit
          </Button>
        </Box>
      </CardActions>
    </Card>
  );
}

const EMPTY_FORM = { name: "", description: "", orientation: "portrait", resolution: "390x844" };

export default function KioskStudioDashboard() {
  const dispatch = useDispatch();
  const api = localStorage.getItem("api");
  const headers = useSelector((state) => state.header);
  const emmsheaders = useSelector((state) => state.emmsheader);
  const emms = useSelector((state) => state.emms);
  const user = useSelector((state) => state.user.user);
  const [selectedOption, setSelectedOption] = useState(emms?.emms[0]?.id);
  const [studioItems, setStudioItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orientation, setOrientation] = useState("portrait");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);

  const [publishOpen, setPublishOpen] = useState(false);
  const [publishItem, setPublishItem] = useState(null);
  const [publishTemplate, setPublishTemplate] = useState(null);
  const [publishScreens, setPublishScreens] = useState([]);
  const [publishForm, setPublishForm] = useState({ name: "", description: "", tagInput: "", tags: [], price: "" });
  const [selectedScreenIds, setSelectedScreenIds] = useState(new Set());
  const [publishing, setPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState("");
  const [publishLoading, setPublishLoading] = useState(false);
  const screenRefs = useRef({});

  const perPage = orientation === "landscape" ? 5 : 6;

  const fetchStudioItems = (value) => {
    const selectedEmms = emms?.emms?.find((element) => element.id === value);
    if (!selectedEmms) return;

    const localEmmsHeaders = {
      headers: {
        "Content-Type": "application/json",
        "x-api-id": selectedEmms?.apiId,
        "x-api-key": selectedEmms?.apiKey,
      },
    };
    dispatch(setCurrentEmms(selectedEmms));
    dispatch(setEmmsHeaders(localEmmsHeaders));

    const requestBody = {
      path: "kioskStudio",
      query: {},
      emmsId: value,
      body: {},
      requestType: "GET",
      headers: localEmmsHeaders.headers,
    };
    setLoading(true);
    axios
      .post(api + `/emms/piggyback`, requestBody, headers)
      .then((response) => {
        setStudioItems(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Failed to fetch kiosk studio:", error);
        setStudioItems([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStudioItems(selectedOption);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => { setPage(1); }, [orientation, search]);

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    fetchStudioItems(event.target.value);
  };

  const options = emms?.emms?.map((emm) => ({ label: emm.name, value: emm.id }));

  const getEmmsRef = (value) => {
    const selectedEmms = emms?.emms?.find((element) => element.id === value);
    return { apiId: selectedEmms?.apiId, apiKey: selectedEmms?.apiKey };
  };

  const handleOpenCreate = () => {
    setCreateForm(EMPTY_FORM);
    setCreateOpen(true);
  };

  const handleCreateSubmit = () => {
    const { apiId, apiKey } = getEmmsRef(selectedOption);

    // Step 1 — create the KioskTemplate + KioskStore mapping
    const storeBody = {
      path: "kioskTemplate",
      query: {},
      emmsId: `${selectedOption}`,
      body: {
        name: createForm.name,
        description: createForm.description,
        orientation: createForm.orientation,
        resolution: createForm.resolution,
        owner: user?.username || "Anonymous",
      },
      requestType: "POST",
      headers: emmsheaders?.emmsheaders?.headers,
    };

    setCreating(true);
    axios
      .post(api + `/emms/piggyback`, storeBody, headers)
      .then(async (storeRes) => {
        const storeItem = storeRes.data;

        // Step 2 — link to KioskStudio
        const studioBody = {
          path: "kioskStudio",
          query: {},
          emmsId: `${selectedOption}`,
          body: {
            templateId: storeItem.id,
            name: createForm.name,
            customProperties: {},
          },
          requestType: "POST",
          headers: emmsheaders?.emmsheaders?.headers,
        };

        return axios
          .post(api + `/emms/piggyback`, studioBody, headers)
          .then((studioRes) => {
            sessionStorage.setItem("kioskStudioRef", JSON.stringify({
              templateId: storeItem.id,
              studioId: studioRes.data?.id,
              emmsId: selectedOption,
              apiId,
              apiKey,
              resolution: createForm.resolution,
              orientation: createForm.orientation,
            }));
            setCreateOpen(false);
            window.open("/kiosk-editor", "_blank");
            fetchStudioItems(selectedOption);
          });
      })
      .catch((error) => {
        console.error("Create failed:", error);
      })
      .finally(() => setCreating(false));
  };

  const handlePublishOpen = async (item) => {
    setPublishItem(item);
    setPublishTemplate(null);
    setPublishScreens([]);
    setSelectedScreenIds(new Set());
    setPublishForm({ name: item.name || "", description: "", tagInput: "", tags: [], price: "" });
    setPublishOpen(true);
    setPublishLoading(true);
    try {
      const reqBody = {
        path: "kioskTemplate/single",
        query: { id: item.templateId },
        emmsId: `${selectedOption}`,
        body: {},
        requestType: "GET",
        headers: emmsheaders?.emmsheaders?.headers,
      };
      const res = await axios.post(api + `/emms/piggyback`, reqBody, headers);
      const tmpl = res.data;
      setPublishTemplate(tmpl);
      const rd = tmpl?.customProperties?.kioskReferenceData;
      if (rd) {
        const parsed = typeof rd === "string" ? JSON.parse(rd) : rd;
        setPublishScreens(parsed.screens || []);
      }
      setPublishForm({
        name: tmpl?.name || item.name || "",
        description: tmpl?.description || "",
        tagInput: "",
        tags: Array.isArray(tmpl?.tags) ? tmpl.tags : [],
        price: tmpl?.price != null ? String(tmpl.price) : "",
      });
    } catch (err) {
      console.error("Failed to fetch template:", err);
    } finally {
      setPublishLoading(false);
    }
  };

  const handlePublishSubmit = async () => {
    setPublishing(true);
    setPublishStatus("");
    try {
      const { apiId, apiKey } = getEmmsRef(selectedOption);
      const authHeader = headers.headers.Authorization;
      const screenIds = [...selectedScreenIds];

      // Step 1 — capture & upload each selected screen
      const screenshotUrls = [];
      for (let i = 0; i < screenIds.length; i++) {
        const screenId = screenIds[i];
        const el = screenRefs.current[screenId];
        if (!el) continue;
        setPublishStatus(`Uploading screenshot ${i + 1} of ${screenIds.length}…`);
        try {
          const dataUrl = await toPng(el, { cacheBust: true });
          const blob = await (await fetch(dataUrl)).blob();
          const formData = new FormData();
          const reqBody = {
            path: "kioskTemplate/screenshot",
            query: { id: publishTemplate.id },
            emmsId: `${selectedOption}`,
            requestType: "POST",
            headers: { "Content-Type": "application/json", "x-api-id": apiId, "x-api-key": apiKey },
          };
          formData.append("jsonData", JSON.stringify(reqBody));
          formData.append("file", blob, `screen_${i + 1}.png`);
          // Use fetch instead of axios so FormData sets the correct multipart boundary
          const uploadRes = await fetch(api + `/emms/piggyback/formdata`, {
            method: "POST",
            headers: { Authorization: authHeader },
            body: formData,
          });
          const uploadData = await uploadRes.json();
          const url = uploadData?.uploadedUrl;
          if (url) screenshotUrls.push(url);
        } catch (e) {
          console.error("Screenshot upload failed for screen", screenId, e);
        }
      }

      // Step 2 — create the store listing
      setPublishStatus("Creating store listing…");
      const storeBody = {
        path: "kioskStore",
        query: {},
        emmsId: `${selectedOption}`,
        body: {
          templateId: publishItem.templateId,
          name: publishForm.name,
          description: publishForm.description,
          price: publishForm.price ? parseFloat(publishForm.price) : 0,
          orientation: publishTemplate?.orientation || "portrait",
          tags: publishForm.tags,
          customProperties: { screenshots: screenshotUrls },
        },
        requestType: "POST",
        headers: emmsheaders?.emmsheaders?.headers,
      };
      await axios.post(api + `/emms/piggyback`, storeBody, headers);

      setPublishStatus("");
      setPublishOpen(false);
      fetchStudioItems(selectedOption);
    } catch (err) {
      console.error("Publish failed:", err);
      setPublishStatus("Publish failed. Please try again.");
    } finally {
      setPublishing(false);
    }
  };

  const buildRef = (item) => {
    const { apiId, apiKey } = getEmmsRef(selectedOption);
    return {
      storeId: item.kioskStoreId,
      templateId: item.templateId,
      studioId: item.id,
      emmsId: selectedOption,
      apiId,
      apiKey,
    };
  };

  const handleEdit = (item) => {
    sessionStorage.setItem("kioskStudioRef", JSON.stringify(buildRef(item)));
    window.open("/kiosk-editor", "_blank");
  };

  const handlePreview = (item) => {
    localStorage.setItem("kioskPreviewRef", JSON.stringify(buildRef(item)));
    window.open("/kiosk-preview", "_blank");
  };

  const handleDelete = (id) => {
    const requestBody = {
      path: "kioskStudio",
      query: { id },
      emmsId: `${selectedOption}`,
      body: {},
      requestType: "DELETE",
      headers: emmsheaders?.emmsheaders?.headers,
    };
    axios
      .post(api + `/emms/piggyback`, requestBody, headers)
      .then(() => setStudioItems((prev) => prev.filter((i) => i.id !== id)))
      .catch((error) => console.error("Delete failed:", error));
  };

  const filtered = studioItems.filter((i) =>
    (i.name || "").toLowerCase().includes(search.toLowerCase())
  );
  const pageCount = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="mainContainer emms-main-container">
      <header style={{ marginLeft: "10%" }}>
        <figure>
          <img className="img content-ai-header-image" src={image} alt="Kiosk Studio" />
        </figure>
      </header>

      <div className="title-container">
        <div className="title">
          <TitleWithDropdown
            title="EMMS Service"
            options={options}
            selectedOption={selectedOption}
            onOptionChange={handleOptionChange}
          />
        </div>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
          <ToggleButtonGroup
            value={orientation}
            exclusive
            size="small"
            onChange={(_, val) => { if (val) setOrientation(val); }}
          >
            <ToggleButton value="portrait" sx={{ textTransform: "none", gap: 0.5, fontSize: 12 }}>
              <StayCurrentPortraitIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="landscape" sx={{ textTransform: "none", gap: 0.5, fontSize: 12 }}>
              <StayCurrentLandscapeIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder="Search designs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 200 } }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{ textTransform: "none" }}
          >
            Create
          </Button>
        </Box>
      </div>

      <div className="emms-main-container-left-align" style={{ marginLeft: "10%", marginTop: "1.5%", marginBottom: "5%" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : studioItems.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 2,
              border: "1px dashed",
              borderColor: "grey.300",
              borderRadius: 2,
              maxWidth: 600,
            }}
          >
            <SmartphoneIcon sx={{ fontSize: 56, color: "text.disabled" }} />
            <Typography color="text.secondary" variant="body2">
              Your studio is empty
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ textTransform: "none" }}
            >
              Create New
            </Button>
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No designs match "{search}"
          </Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
              {paginated.map((item) => (
                <StudioCard
                  key={item.id}
                  item={item}
                  onEdit={handleEdit}
                  onPreview={handlePreview}
                  onDelete={handleDelete}
                  onPublish={handlePublishOpen}
                  orientation={orientation}
                />
              ))}
            </Box>
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </div>

      {/* ── Publish to Store modal ── */}
      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Publish to Kiosk Store</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          {publishLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Name"
                  fullWidth
                  size="small"
                  required
                  error={!publishForm.name.trim()}
                  helperText={!publishForm.name.trim() ? "Name is required" : ""}
                  value={publishForm.name}
                  onChange={(e) => setPublishForm((f) => ({ ...f, name: e.target.value }))}
                  sx={{ minWidth: 160, flex: { xs: "1 1 100%", sm: 1 } }}
                />
                <TextField
                  label="Price"
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={publishForm.price}
                  onChange={(e) => setPublishForm((f) => ({ ...f, price: e.target.value }))}
                  sx={{ width: { xs: "100%", sm: 140 } }}
                />
              </Box>
              <TextField
                label="Description"
                fullWidth
                size="small"
                required
                multiline
                rows={2}
                error={!publishForm.description.trim()}
                helperText={!publishForm.description.trim() ? "Description is required" : ""}
                value={publishForm.description}
                onChange={(e) => setPublishForm((f) => ({ ...f, description: e.target.value }))}
              />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                  Tags
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                  {publishForm.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={() => setPublishForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }))}
                    />
                  ))}
                  <TextField
                    size="small"
                    placeholder="Add tag + Enter"
                    value={publishForm.tagInput}
                    onChange={(e) => setPublishForm((f) => ({ ...f, tagInput: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && publishForm.tagInput.trim()) {
                        e.preventDefault();
                        setPublishForm((f) => ({
                          ...f,
                          tags: [...new Set([...f.tags, f.tagInput.trim()])],
                          tagInput: "",
                        }));
                      }
                    }}
                    sx={{ width: 160 }}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Screenshots{" "}
                  <Typography component="span" variant="caption" color="error">*</Typography>
                  {" "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    — select screens to capture & upload as store previews
                  </Typography>
                </Typography>
                {publishScreens.length === 0 ? (
                  <Box sx={{ py: 2, px: 1, border: "1px dashed", borderColor: "error.main", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" color="error">
                      No screens found. Open the editor and save your design first.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1, pt: 0.5 }}>
                      {publishScreens.map((screen) => (
                        <ScreenThumb
                          key={screen.id}
                          screen={screen}
                          selected={selectedScreenIds.has(screen.id)}
                          onToggle={() =>
                            setSelectedScreenIds((prev) => {
                              const next = new Set(prev);
                              next.has(screen.id) ? next.delete(screen.id) : next.add(screen.id);
                              return next;
                            })
                          }
                          onRef={(id, el) => { screenRefs.current[id] = el; }}
                        />
                      ))}
                    </Box>
                    {selectedScreenIds.size === 0 && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                        Select at least one screenshot
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: "column", alignItems: "stretch", gap: 1 }}>
          {publishStatus && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5 }}>
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">{publishStatus}</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setPublishOpen(false)} disabled={publishing} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handlePublishSubmit}
              disabled={!publishForm.name.trim() || !publishForm.description.trim() || selectedScreenIds.size === 0 || publishing || publishLoading}
              sx={{ textTransform: "none" }}
              startIcon={<StorefrontIcon />}
            >
              {publishing ? "Publishing…" : "Publish to Store"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── Create modal ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>New Kiosk Design</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <Typography variant="caption" color="text.secondary">
            All details can be changed later inside the editor.
          </Typography>
          <TextField
            label="Name"
            fullWidth
            size="small"
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="Description"
            fullWidth
            size="small"
            multiline
            rows={2}
            value={createForm.description}
            onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Orientation</InputLabel>
            <Select
              value={createForm.orientation}
              label="Orientation"
              onChange={(e) => setCreateForm((f) => ({ ...f, orientation: e.target.value }))}
            >
              <MenuItem value="portrait">Portrait</MenuItem>
              <MenuItem value="landscape">Landscape</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Resolution</InputLabel>
            <Select
              value={createForm.resolution}
              label="Resolution"
              onChange={(e) => setCreateForm((f) => ({ ...f, resolution: e.target.value }))}
            >
              {RESOLUTIONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Resolution sets the canvas size in the editor — width and height are fully adjustable there too.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={!createForm.name.trim() || creating}
            sx={{ textTransform: "none" }}
          >
            {creating ? "Creating…" : "Create & Open Editor"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
